import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { actions as locationTaskActions } from '@Actions/locationTaskActions';
import * as types from '@Constants/actionTypes';
import Notification from '@Common/Notification';
import MentionableTextArea from '@Common/MentionableTextArea/MentionableTextArea';
import LoadingIndicator from '@Common/LoadingIndicator/LoadingIndicator';
import CommentRow from './CommentRow';
import { CommentDetailsProps, MentionableUser } from './Types';
import './Comments.scss';

interface CommentsProps {
    locationTaskId: number;
    locationKey: string;
}

const Comments = (props: CommentsProps): ReactElement => {

    const pageSize = 3;

    const { locationTaskId, locationKey } = props;

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [comment, setComment] = useState<string>('');
    const [comments, setComments] = useState<CommentDetailsProps[]>([]);
    const [commentCount, setCommentCount] = useState<number>(0);
    const [mentionableUsers, setMentionableUsers] = useState<MentionableUser[]>([]);
    const [pageNumber, setPageNumber] = useState<number>(1);

    const dispatch: Dispatch = useDispatch();
    const getComments = async (taskId: number, page: number, size: number) => dispatch<any>(locationTaskActions.loadCommentsPage(taskId, page, size));
    const getMentionableUsers = async (locationKey: string) => dispatch<any>(locationTaskActions.listMentionableUsers(locationKey));
    const createComment = async (taskId: number, comment: string) => dispatch<any>(locationTaskActions.createComment(taskId, comment));
    const updateComment = async (commentId: number, comment: string) => dispatch<any>(locationTaskActions.updateComment(commentId, comment));

    const loadComments = async (pageNum: number) => {
        if (locationTaskId <= 0) return;
        setIsLoading(true);

        try {
            const response = await getComments(locationTaskId, pageNum, pageSize);
            if (response.type === types.LOAD_LOCATION_TASK_COMMENTS_SUCCESS) {
                pageNum === 1
                    ? setComments(response.payload.comments as CommentDetailsProps[])
                    : setComments([...comments, ...response.payload.comments as CommentDetailsProps[]]);
                setCommentCount(response.payload.total as number);
            } else if (response.type === types.LOAD_LOCATION_TASK_COMMENTS_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setIsLoading(false);
        }
    };

    const loadMentionableUsers = async () => {
        setIsLoading(true);

        try {
            const response = await getMentionableUsers(locationKey);
            if (response.type === types.SEARCH_LOCATION_TASK_MENTIONABLE_USERS_SUCCESS) {
                setMentionableUsers(response.payload.users);
            } else if (response.type === types.SEARCH_LOCATION_TASK_MENTIONABLE_USERS_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setIsLoading(false);
        }
    };

    const onCommentCreate = async (value: string) => {
        try {
            const response = await createComment(locationTaskId, value);
            if (response.type === types.CREATE_LOCATION_TASK_COMMENT_SUCCESS) {
                Notification('success', 'Success', 'Comment created successfully');
                setComment('');
                setPageNumber(1);
                loadComments(1);
            } else if (response.type === types.CREATE_LOCATION_TASK_COMMENT_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setIsLoading(false);
        }
    };

    const onCommentUpdate = async (commentId: number, value: string) => {
        setIsLoading(true);

        try {
            const response = await updateComment(commentId, value);
            if (response.type === types.UPDATE_LOCATION_TASK_COMMENT_SUCCESS) {
                Notification('success', 'Success', 'Comment updated successfully');
                return response.payload;
            } else if (response.type === types.UPDATE_LOCATION_TASK_COMMENT_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setIsLoading(false);
        }
    }

    const showMoreComments = async () => {
        const nextPage = pageNumber + 1;
        setPageNumber(nextPage);
        await loadComments(nextPage);
    }

    useEffect(() => {
        loadComments(pageNumber);
        loadMentionableUsers();
    }, [locationTaskId]);

    return (
        <LoadingIndicator isLoading={isLoading}>
            <div className='location-task-comments'>
                <div className='row'>
                    <div className='col-12'>
                        <div className='new-comment-container w-100'>
                            <MentionableTextArea
                                name='comment'
                                data={mentionableUsers}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                maxLength={2000}
                                placeHolder='What would you like to say? Type @ to tag a user in your comment.'
                            />
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>

                                <button type='button' title='Post' className='pacer-table-button post-button' onClick={(e) => { onCommentCreate(comment); }}>Post</button>
                            </div>
                        </div>
                    </div>
                </div>

                {comments?.map((comment: CommentDetailsProps) => {
                    return <CommentRow key={comment.id} comment={comment} mentionableUsers={mentionableUsers} onCommentPost={onCommentUpdate} />;
                })}

                <div className='row'>
                    <div className='col-12 text-center'>
                        {
                            comments.length < commentCount &&
                            <div className="link-with-icon" onClick={() => showMoreComments()}>
                                <i className={'far fa-chevron-down'}></i>
                                <button type='button' title="Show More" className="btn btn-link">
                                    Show More
                                </button>
                            </div>
                        }
                    </div>
                </div>
            </div>
        </LoadingIndicator>
    );
};

export default Comments;
